import type { IImageDataSource } from "@/data/dao/service/BaseServiceDao";
import { Misc } from "@/data/util/Misc";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { UiNotification } from "@/portal/ui/Notification";

export class ImagePicker<T> {
    private $element: JQuery;
    private $preview: JQuery;
    private $input: JQuery;
    private dataSource: IImageDataSource<T>;
    public pendingFile?: File;
    public hasPendingDelete?: boolean;
    private imagePath?: string;

    constructor(dataSource: IImageDataSource<T>) {
        this.$element = $('<div class="imagepicker" />');
        this.$preview = $('<div class="preview" />').appendTo(this.$element);
        this.$input = $('<input type="file" />').appendTo(this.$element);
        this.$input.change(() => {
            this.validateAndSet(this.$input.prop("files"));
        });

        this.dataSource = dataSource;

        this.$preview.unbind("click").bind("click", () => {
            this.$input.trigger("click");
        });
    }

    public setValue(m?: T): ImagePicker<T> {
        const v = m === undefined ? undefined : this.dataSource.valueAccessor(m, undefined);
        if (v === undefined) {
            // the value carries no image, so there is none for a cancelled pick to go back to
            this.imagePath = undefined;
            this.setEmpty();
        } else this.setExisting(v);

        return this;
    }
    
    public getPendingFile(): File | undefined {
        return this.pendingFile;
    }

    public handle(m: T): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            if (this.pendingFile !== undefined) {
                return this.dataSource
                    .create(m, this.pendingFile)
                    .then((s) => {
                        this.dataSource.valueAccessor(m, s);
                        resolve(m);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            } else if (this.hasPendingDelete) {
                return this.dataSource
                    .remove(m)
                    .then(() => {
                        this.dataSource.valueAccessor(m, null);
                        resolve(m);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            } else {
                resolve(m);
            }
        });
    }

    public renderTo($element: JQuery): ImagePicker<T> {
        this.$element.appendTo($element);

        return this;
    }

    public setHasError(hasError: boolean) {
        if (hasError === true) {
            this.$element.addClass("is-invalid");
        } else {
            this.$element.removeClass("is-invalid");
        }
    }

    private setEmpty() {
        this.pendingFile = undefined;
        this.$preview.html('<div class="actbtn"><i class="la la-plus"></i></div>');
        this.$preview.append(
            '<div class="hint"><div class="label">' + i18n("elements.imagePicker.noImage") + "</div></div>"
        );
    }

    private setExisting(path: string) {
        this.imagePath = path;
        this.$preview.html('<div class="actbtn"><i class="la la-pencil"></i></div>');
        this.$preview.append('<img src="' + this.imagePath + '" />');
        this.pendingFile = undefined;
        this.hasPendingDelete = false;

        const deleteOverlay = $('<div class="overlay" />').appendTo(this.$preview);
        $('<div class="actbtn"><i class="la la-trash"></i></div>')
            .appendTo(deleteOverlay)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.hasPendingDelete = true;
                this.setEmpty();
            });
    }

    public setPendingUpload(file: File) {
        this.pendingFile = file;
        this.hasPendingDelete = false;
        this.$preview.html('<div class="actbtn"><i class="la la-pencil"></i></div>');

        const reader = new FileReader();
        reader.onload = () => {
            this.$preview.append('<img src="' + reader.result + '" />');
        };
        reader.readAsDataURL(file);

        const deleteOverlay = $('<div class="overlay" />').appendTo(this.$preview);
        $('<div class="actbtn"><i class="la la-times-circle"></i></div>')
            .appendTo(deleteOverlay)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                if (this.imagePath !== undefined) this.setExisting(this.imagePath);
                else this.setEmpty();
            });
    }

    private validateAndSet(fileList: FileList) {
        const file = fileList[0];
        if (file === undefined) return;

        const maxAllowedFileSizeInBytes = 5000000; //in bytes 5mb

        if (!file.type.includes("image")) {
            UiNotification.showException(i18n("elements.imagePicker.pleaseSelectImage"));
            return;
        }

        if (file.size > maxAllowedFileSizeInBytes) {
            UiNotification.showException(
                i18n("elements.imagePicker.invalidSize", { size: Misc.byteToReadableString(maxAllowedFileSizeInBytes) })
            );
            return;
        }

        this.setPendingUpload(file);
    }
}
